import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LaunchModal from '../components/LaunchModal';

const HomePage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="bg-gray-950 text-white antialiased">
      {/* Hero Section */}
      <section 
        className="relative flex flex-col justify-center overflow-hidden min-h-[100svh] md:min-h-[100vh]"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(102,125,233,0.1),transparent_70%)]"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 flex items-center pt-0 -mt-8 sm:pt-0 sm:-mt-24">
          <div className="text-center space-y-8 max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-gray-800/50 border border-gray-700 rounded-full px-4 py-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300">Finally, horse farm software built for this century!</span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight">
              Modern Horse Farm Management
              <span className="brand-text-gradient"> Made Simple</span>
            </h1>
            
            <p className="text-xl text-gray-400 leading-relaxed max-w-2xl mx-auto">
              Professional billing, records, and management that works on any device. 
              No more outdated interfaces or complicated software.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="brand-gradient text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Get Notified at Launch
              </button>
              <Link 
                to="/contact" 
                className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-3 rounded-lg font-medium transition-all duration-200"
              >
                See It In Action
              </Link>
            </div>
            
            <div className="hidden sm:flex items-center justify-center space-x-8 text-sm text-gray-500 pt-8">
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
                <span>Works on Any Device</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
                <span>English & Spanish</span>
              </div>
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
                <span>Built by Horse People</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Scroll Down Arrow */}
        <div className="hidden sm:block absolute bottom-4 sm:bottom-24 left-1/2 transform -translate-x-1/2 animate-bounce">
          <button 
            onClick={() => window.scrollTo({
              top: window.innerHeight - 80,
              behavior: 'smooth'
            })}
            className="text-gray-400 hover:text-white transition-colors duration-200"
            aria-label="Scroll down to learn more"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </button>
        </div>
      </section>

      {/* Mobile-First Experience */}
      <section className="py-20 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  Manage Your Farm From Anywhere
                </h2>
                <p className="text-lg text-gray-400">
                  BarnBills works seamlessly on phones, tablets, and computers. Your entire operation in your pocket.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white mb-1">Add charges instantly from the barn</h3>
                    <p className="text-sm text-gray-400">Trainers and staff can add vet visits, farrier work, and lessons right from their phones.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white mb-1">Works offline when cell service is poor</h3>
                    <p className="text-sm text-gray-400">Log information even in dead zones. Everything syncs when you're back online.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white mb-1">Access complete horse records anywhere</h3>
                    <p className="text-sm text-gray-400">Vet visits, vaccinations, training notes - everything at your fingertips.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 brand-gradient rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white mb-1">No proprietary hardware needed</h3>
                    <p className="text-sm text-gray-400">Use the devices you already own. iPhone, iPad, Android, Windows - it all works.</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Mockups */}
            <div className="relative">
              <div className="flex justify-center items-center">
                <img 
                  src="/mockup.png" 
                  alt="Mockup of Iphone and Ipad with BarnBills Software loaded on it" 
                  className="max-w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Software Demo Video */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-white">
              See BarnBills in Action
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Watch how easy it is to manage your entire horse operation with modern, intuitive software.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden shadow-2xl">
              <div className="aspect-video">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/1p3HNHDSmA0"
                  title="BarnBills Software Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose BarnBills */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-20">
            <h2 className="text-4xl font-bold text-white">
              Why Horse Farms Choose 
              <span className="brand-text-gradient"> BarnBills</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              While competitors look like they were built in 1985, BarnBills brings modern design 
              and functionality that actually makes sense for today's horse operations.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 items-stretch">
            {/* Core Features */}
            <div className="lg:col-span-2 flex flex-col space-y-8">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 flex-1">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Automated Billing That Actually Works</h3>
                    <p className="text-gray-400 mb-5">Stop spending hours on manual invoicing. BarnBills automatically generates professional invoices for board, training, lessons, and add-on services. Built-in Stripe integration means you get paid faster with credit cards and ACH transfers.</p>
                    <ul className="space-y-3 text-sm text-gray-500">
                      <li className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                        <span>Automatic recurring board charges</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                        <span>Real credit card and ACH processing</span>
                      </li>
                      <li className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                        <span>Professional invoices that reflect well on your operation</span>
                      </li>
                       <li className="flex items-center space-x-2">
                        <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                        <span>Gentle payment reminders and overdue tracking</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 flex-1">
                <div className="flex items-start space-x-6">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-4">Works on Any Device</h3>
                    <p className="text-gray-400 mb-5">Our Progressive Web App works on iPhones, iPads, Android devices, and computers without requiring proprietary hardware. The offline-first design means it works even when your barn's cell service is spotty.</p>
                    <div className="grid grid-cols-2 gap-6 text-sm">
                      <div className="space-y-3 text-gray-500">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                          <span>iPhone, iPad, Android, Windows</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                          <span>No proprietary hardware needed</span>
                        </div>
                      </div>
                      <div className="space-y-3 text-gray-500">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                          <span>Works offline, syncs when online</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                          </svg>
                          <span>Team access from anywhere</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Side Features */}
            <div className="flex flex-col space-y-6">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <div className="w-10 h-10 brand-gradient rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                  </svg>
                </div>
                <h3 className="font-semibold text-white mb-2">Seamless Horse Transfers</h3>
                <p className="text-sm text-gray-400">Industry-first feature: transfer horses between farms with complete history intact. No more starting from scratch when horses move.</p>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <div className="w-10 h-10 brand-gradient rounded-lg flex items-center justify-center mb-4">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
                  </svg>
                </div>
                <h3 className="font-semibold text-white mb-2">Free Plugin Architecture</h3>
                <p className="text-sm text-gray-400">Start simple, grow advanced. Add scheduling, inventory management, vet records, and more with free plugins as your operation scales.</p>
              </div>
              
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
                <div className="w-10 h-10 brand-gradient rounded-lg flex items-center justify-center mb-4">
                   <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9"></path>
                    </svg>
                </div>
                <h3 className="font-semibold text-white mb-2">Multilingual Support</h3>
                <p className="text-sm text-gray-400">Full English and Spanish interfaces make it easy for diverse teams to use the software in their preferred language.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modern vs Legacy Comparison */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Stop Embarrassing Yourself With 1985 Software</h2>
            <p className="text-gray-400">Your operation deserves better than clunky, outdated systems</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Legacy Software */}
            <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-8">
              <div className="mb-6">
                <img 
                  src="/1985.png" 
                  alt="Old 1985 computer representing outdated farm management software" 
                  className="w-full object-contain rounded-lg"
                />
              </div>
              <h3 className="text-xl font-semibold text-red-300 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                </svg>
                1985-Era Competitors
              </h3>
              <ul className="space-y-3 text-red-300">
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Looks like it was designed in the 1980s</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Requires expensive proprietary hardware</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Hundreds-page manuals to learn basic functions</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Doesn't work on mobile devices</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Complex, overwhelming interfaces</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-red-500 mt-1">×</span>
                  <span>Embarrassing to show clients and vets</span>
                </li>
              </ul>
            </div>
            
            {/* BarnBills */}
            <div className="bg-green-900/20 border border-green-800/50 rounded-xl p-8">
              <div className="mb-6">
                <img 
                  src="/modern.png" 
                  alt="Modern computer with BarnBills software displaying professional interface" 
                  className="w-full object-contain"
                />
              </div>
              <h3 className="text-xl font-semibold text-green-300 mb-6 flex items-center">
                <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                BarnBills
              </h3>
              <ul className="space-y-3 text-green-300">
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Modern, professional design you're proud to show</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Works on any device you already own</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Intuitive interface anyone can learn in minutes</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Mobile-first design for barn work</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Simple by default, advanced when needed</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-400 mt-1">✓</span>
                  <span>Software that reflects well on your operation</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Enterprise-Grade Security You Can Trust</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Your farm's data deserves the highest level of protection. That's why BarnBills is built on Hybrid Cloud architecture with industry-leading security certifications.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Built on Hybrid Cloud</h3>
                    <p className="text-gray-400">
                      BarnBills is built on Hybrid Cloud architecture, powered by hybrid hosting infrastructure that is certified for ISO/IEC 27001, 27017, and 27018 compliance. 
                      We inherit enterprise-grade security protections for data storage, encryption, and cloud management.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-3">Tailored Security Controls</h3>
                    <p className="text-gray-400">
                      On top of our hybrid hosting foundation, we enforce strict access controls, data validation, and privacy measures 
                      specifically designed for horse farms and equine businesses.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Certifications */}
            <div className="space-y-6">
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-white mb-6 text-center">Security Certifications</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 bg-gray-900 rounded-lg p-4">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-white font-medium text-sm">GDPR</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-gray-900 rounded-lg p-4">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-white font-medium text-sm">SOC 1, 2, 3</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-gray-900 rounded-lg p-4">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-white font-medium text-sm">ISO/IEC 27018</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-gray-900 rounded-lg p-4">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-white font-medium text-sm">ISO/IEC 27017</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 bg-gray-900 rounded-lg p-4 col-span-2">
                    <svg className="w-5 h-5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                    <span className="text-white font-medium text-sm">ISO/IEC 27001</span>
                  </div>
                </div>
                
                <div className="mt-6 pt-6 border-t border-gray-700">
                  <div className="flex items-center justify-center space-x-2 text-gray-400">
                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                    <span className="text-sm">Powered by Hybrid Hosting</span>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <Link 
                  to="/security" 
                  className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  <span>Learn more about our security practices</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases - Who It's For */}
      <section className="pt-24 pb-12 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Built for Every Type of Horse Operation</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Whether you run a small boarding stable or a large training facility, BarnBills scales to fit your operation.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Boarding Barns</h3>
              <p className="text-sm text-gray-400">Automate monthly board billing and track add-on services seamlessly</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Training Facilities</h3>
              <p className="text-sm text-gray-400">Manage lesson schedules, training packages, and horse progress records</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Breeding Operations</h3>
              <p className="text-sm text-gray-400">Track breeding records, foaling schedules, and registration paperwork</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Lesson Barns</h3>
              <p className="text-sm text-gray-400">Schedule lessons, track student progress, and manage instructor payments</p>
            </div>
          </div>
          

        </div>
      </section>

      {/* Farm Types & Sizes */}
      <section className="pt-12 pb-24 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <h3 className="text-xl text-gray-400 mb-4">
              No Matter Your Farm Size - From 5 Horses to 5 Thousand Horses
            </h3>
            <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
              Whether you run a small jumping stable, a large thoroughbred racing operation, a dressage facility, 
              a western training barn, or a boarding stable, BarnBills adapts to your specific needs and grows with your operation.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-gray-300">
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Jumping Stables</span>
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Thoroughbred Racing</span>
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Dressage Facilities</span>
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Western Training</span>
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Boarding Stables</span>
              <span className="bg-gray-800/50 px-4 py-2 rounded-lg">Breeding Operations</span>
            </div>
            <div className="pt-8">
              <Link 
                to="/contact" 
                className="inline-flex items-center space-x-2 brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                <span>See How BarnBills Fits Your Operation</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Choose the plan that fits your operation. No hidden fees, no complicated contracts. 
              Cancel anytime.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Starter Plan */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 relative">
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-white mb-2">Starter</h3>
                <p className="text-gray-400 mb-4">Perfect for smaller operations</p>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-2xl text-gray-500 line-through">$40</span>
                  <span className="text-4xl font-bold text-white">$35</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-blue-400 mt-2">Launch pricing - save $60/year!</p>
              </div>
              
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 15 horses</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 10 clients</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Up to 5 team members</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>All core features</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Email support</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-500">Free data migration</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-500">Custom integrations</span>
                </li>
              </ul>
              
              <button 
                onClick={() => setIsModalOpen(true)}
                className="w-full text-center border border-gray-600 text-gray-300 hover:text-white hover:border-gray-500 px-6 py-3 rounded-lg font-medium transition-all duration-200"
              >
                Get Notified at Launch
              </button>
            </div>
            
            {/* Unlimited Plan */}
            <div className="bg-gray-800 border-2 border-blue-500 rounded-xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="brand-gradient text-white px-4 py-1 rounded-full text-sm font-medium">
                  Most Popular
                </span>
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-white mb-2">Unlimited</h3>
                <p className="text-gray-400 mb-4">For growing and large operations</p>
                <div className="flex items-center justify-center space-x-2">
                  <span className="text-2xl text-gray-500 line-through">$85</span>
                  <span className="text-4xl font-bold text-white">$75</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <p className="text-sm text-blue-400 mt-2">First year pricing - save $120!</p>
              </div>
              
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited horses</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited clients</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited team members</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>All features + plugins</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Priority phone & email support</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Free data migration</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span className="text-gray-400">Basic custom integrations</span>
                </li>
              </ul>
              
              <button 
                onClick={() => setIsModalOpen(true)}
                className="w-full text-center brand-gradient text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Get Notified at Launch
              </button>
            </div>
            
            {/* Ranch Scale Plan */}
            <div className="bg-gray-800 border border-purple-500 rounded-xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Enterprise Scale
                </span>
              </div>
              
              <div className="text-center mb-8">
                <h3 className="text-xl font-semibold text-white mb-2">Enterprise Scale</h3>
                <p className="text-gray-400 mb-4">For major operations (1000+ horses)</p>
                <div className="flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">Custom</span>
                </div>
                <p className="text-sm text-purple-400 mt-2">Tailored to your operation</p>
              </div>
              
              <ul className="space-y-3 mb-8 text-gray-300">
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Unlimited everything</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Multi-location support</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Advanced reporting & analytics</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Dedicated account manager</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>White-label options</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>Full custom integrations</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  <span>24/7 priority support</span>
                </li>
              </ul>
              
              <Link 
                to="/contact" 
                className="w-full text-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25 block"
              >
                Contact Sales
              </Link>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <p className="text-gray-400 mb-4">
              All plans include: Mobile apps, credit card processing, ACH bank transfers (0.8% capped at $5), 
              <strong> free mailed check management</strong>, automatic invoicing, data exports, and 99.9% uptime guarantee
            </p>
            <p className="text-sm text-gray-500">
              No setup fees • Cancel anytime • 30-day money-back guarantee
            </p>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Accept Payments Your Way</h2>
            <p className="text-gray-400">
              Flexible payment options that work for every client preference
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Credit & Debit Cards</h3>
              <p className="text-sm text-gray-400 mb-3">Standard processing at 2.9% + 30¢</p>
              <p className="text-xs text-gray-500">Visa, Mastercard, American Express, Discover</p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">ACH Bank Transfer</h3>
              <p className="text-sm text-gray-400 mb-3">Low cost at 0.8% capped at $5</p>
              <p className="text-xs text-gray-500">Direct from client's bank account</p>
            </div>
            
            <div className="bg-gray-900 border border-green-800 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Mailed Checks</h3>
              <p className="text-sm text-green-400 mb-3">Completely FREE - $0 fees</p>
              <p className="text-xs text-gray-500">Automated tracking & deposit reminders</p>
            </div>
          </div>
          
          <div className="text-center mt-8">
            <p className="text-gray-400 text-sm">
              All payment methods integrate seamlessly with automated invoicing and client portal access.
              <br />No additional fees from BarnBills - you only pay standard processing rates.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-gray-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
            <p className="text-gray-400">
              Get answers to common questions about BarnBills
            </p>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">When will BarnBills be available?</h3>
              <p className="text-gray-400">
                We're currently in beta testing with select farms and plan to launch publicly in early 2025. 
                Sign up to be notified when we're ready for your farm!
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Can I migrate my existing data from other software?</h3>
              <p className="text-gray-400">
                Yes! We provide free data migration for Unlimited plan customers. Our team will work with you 
                to transfer your horse records, client information, and financial data from most major barn management systems.
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">What devices does BarnBills work on?</h3>
              <p className="text-gray-400">
                BarnBills works on any device with a web browser - iPhone, iPad, Android phones and tablets, 
                Windows computers, Mac, and Chromebooks. No app store downloads required.
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Do you charge transaction fees for payments?</h3>
              <p className="text-gray-400">
                BarnBills doesn't add any fees on top of standard payment processing. You'll pay standard Stripe rates 
                (2.9% + 30¢ for cards, 0.8% for ACH capped at $5) with no markup from us. <strong>Mailed checks are completely free</strong> – 
                we provide automated check management with tracking and deposit reminders.
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">What happens if I need to cancel?</h3>
              <p className="text-gray-400">
                You can cancel anytime with no penalties. Your data remains accessible for 90 days after cancellation, 
                and we'll provide a complete export of all your information.
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Is my farm's data secure?</h3>
              <p className="text-gray-400">
                Absolutely. BarnBills is built on enterprise-grade hybrid cloud infrastructure with bank-level encryption, 
                SOC 2 compliance, and regular security audits. Your data is backed up automatically and stored securely.
              </p>
            </div>
            
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-3">Can I try BarnBills before committing?</h3>
              <p className="text-gray-400">
                Yes! We offer a 30-day money-back guarantee and will be offering free trials once we launch. 
                Beta testers get early access and special launch pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Features Grid */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">Everything You Need to Run a Professional Operation</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Smart Billing</h3>
              <p className="text-sm text-gray-400">Automated invoicing with real payment processing</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Mobile First</h3>
              <p className="text-sm text-gray-400">Works perfectly on any device, even offline</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0V9a1 1 0 011-1h4a1 1 0 011 1v11"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Complete Records</h3>
              <p className="text-sm text-gray-400">All horse data organized and accessible</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 brand-gradient rounded-lg flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-white mb-2">Plugin System</h3>
              <p className="text-sm text-gray-400">Start simple, add features as you grow</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-white">
              Ready for Modern Farm Management?
            </h2>
            <p className="text-xl text-gray-400">
              Join the hundreds of horse farms that are ready to make the switch to modern, 
              professional farm management. Your operation deserves better.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="brand-gradient text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25"
              >
                Get Notified at Launch
              </button>
              <Link 
                to="/contact" 
                className="border border-gray-700 text-gray-300 hover:text-white hover:border-gray-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-200"
              >
                Join Beta Testing
              </Link>
            </div>
            <p className="text-gray-500 text-sm">
              Be the first to experience modern horse farm management • Built by horse people
            </p>
          </div>
        </div>
      </section>

      {/* Launch Modal */}
      <LaunchModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </div>
  );
};

export default HomePage; 